import 'firebase/compat/firestore'
import firebase from 'firebase/compat/app'
import { ActionContext, Module } from 'vuex'
import { MessageTypeString, MessageType } from '@/models/message'
import {
  Contact,
  Conversation,
  ConversationBulkActionTypes,
  ConversationFilterType,
  IConversationBulkAction,
  Link,
} from '../models'
import { ConversationState, RootState } from './state_models'
import { ConversationsService, IntegrationService, PhoneSystemService } from '../services'

import ConversationParticipants from '../models/conversation_participants'
import { NEW_MESSAGE_FLOW_CONVERSATION_ID } from '../util/constants'
import sortedIndexBy from 'lodash-es/sortedIndexBy'

let unsubscribeRealtime: () => void
let cancelParticipantSubscription: () => void
let searchDebouncer: any
let firstSnapshot: boolean
let locationId: string | undefined
let seqFetching = 0
const limit = 20
let draftsDebouncer: any
let fakeBulkActionDebouncer: any
const camelToSnakeCase = str => str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)

const isComingFromSearch = (filters: any) => {
  if (filters.status != ConversationFilterType.TYPE_ALL) {
    return true
  }

  return filters.lastMessageType != null || filters.assignedTo != null || filters.query?.length > 2
}

const checkIfConversationToBeRemoved = (state: any, data: any, user?: any) => {
  //if filters have unread and data doesn't have unread_count > 0
  //if filters have read and data have unread_count > 0
  //if filters have starred and data's starred is false
  //if filters doesn't have starred and data's starred is true
  //if filters lastMessageType and data's lastMessageType is different than it
  //assignedTo filter
  if ([6, 'TYPE_INTERNAL_CHAT', 5, 'TYPE_GROUP_SMS'].includes(data.type)) {
    // if the conversation is internal chat, or group chat, and the user is the one who created it, then don't remove it
    // if (data.user_id === user?.id) return false
    return false
  }
  if (data.last_message_type == 'TYPE_NO_SHOW') return true

  if (user && user.permissions) {
    if (!user.permissions?.reviews_enabled && (data.type == 4 || data.type == 'TYPE_REVIEW')) return true
  }
  if (data.deleted) {
    return true
  }

  if (
    state.filtersV2.status == 'unread' &&
    (typeof data.unread_count == 'undefined' || data.unread_count == null || data.unread_count == 0) &&
    !state.outboundSentConversationIds?.includes(data.id)
  ) {
    return true
  }

  if (state.filtersV2.status == 'read' && typeof data.unread_count == 'number' && data.unread_count > 0) {
    return true
  }

  if (
    state.filtersV2.status == 'starred' &&
    (typeof data.starred == 'undefined' || (typeof data.starred == 'boolean' && !data.starred))
  ) {
    return true
  }

  if (state.filtersV2.status == 'recents' && !data.inbox) {
    return true
  }

  if (state.filtersV2.lastMessageType && state.filtersV2.lastMessageType != data.last_message_type) {
    return true
  }

  if (state.filtersV2.assignedTo && state.filtersV2.assignedTo.length > 0) {
    if (state.filtersV2.assignedTo == 'unassigned') {
      if (data.assigned_to && data.assigned_to.length > 0) return true

      return false
    } else {
      const assigneeList = state.filtersV2.assignedTo.split(',')

      if (data.assigned_to && data.assigned_to.length > 0) {
        if (assigneeList.includes(data.assigned_to)) return false
        return true
      }
      return true
    }
  }

  if (state.filtersV2.followers?.length) {
    const followedByFilter = state.filtersV2.followers?.split(',') || []
    const updatedFollowers = data.followers || []
    return !updatedFollowers.some(uf => followedByFilter.includes(uf))
  }

  if (state.filtersV2.lastMessageDirection && state.filtersV2.lastMessageDirection !== data.last_message_direction) {
    return true
  }

  if (state.filtersV2.lastMessageAction && state.filtersV2.lastMessageAction != data.last_outbound_message_action) {
    return true
  }

  return false
}

const convertObjectKeysCamelCaseToSnakeCase = (obj: any): any => {
  if (typeof obj == 'undefined' || obj == null) {
    return obj
  }
  if (typeof obj == 'object') {
    if (Array.isArray(obj)) {
      const newObj: any[] = []
      for (const item of obj) {
        newObj.push(convertObjectKeysCamelCaseToSnakeCase(item))
      }
      return newObj
    } else {
      const newObj: any = {}
      Object.keys(obj).forEach(key => {
        if (typeof obj[key] == 'object') {
          newObj[camelToSnakeCase(key)] = convertObjectKeysCamelCaseToSnakeCase(obj[key])
        } else {
          newObj[camelToSnakeCase(key)] = obj[key]
        }
      })
      return newObj
    }
  } else {
    return obj
  }
}

function getSortedIndex(
  conversations: Array<{ [key: string]: any }>,
  element: { [key: string]: any },
  sortBy: string
): number {
  return sortedIndexBy(conversations, element, value => -value[sortBy])
}

function addUpdateContact(
  documents: Array<{ [key: string]: any }>,
  params: {
    currentTab: string
    payload: { [key: string]: any }
    dontAdd?: boolean
  },
  filtersV2: any
) {
  let scoring
  const index = documents.findIndex(obj => obj.id === params.payload.id)
  const newDocuments = [...documents]
  if (index !== -1) {
    scoring = { ...newDocuments[index].scoring }
    newDocuments.splice(index, 1)
  } else if (params.dontAdd) {
    return null
  }

  const sortBy = filtersV2.sortBy ?? 'last_message_date'
  const newPayload = {
    ...params.payload,
    scoring: scoring ?? params.payload.scoring,
  }
  const sortedIndex = getSortedIndex(newDocuments, newPayload, sortBy)
  if (newDocuments.length < limit || sortedIndex !== newDocuments.length) {
    newDocuments.splice(sortedIndex, 0, Object.freeze(newPayload))
  }

  return newDocuments
}

function checkForDuplicacy(documents: any[]) {
  const map = new Map()
  const newDocuments: any[] = []
  for (const doc of documents) {
    if (!map[doc.id]) {
      newDocuments.push(doc)
      map[doc.id] = true
    }
  }

  return newDocuments
}

function removeContact(documents, params: { currentTab: string; payload: { [key: string]: any } }) {
  const index = documents.findIndex(obj => obj.id === params.payload.id)
  if (index !== -1) {
    documents.splice(index, 1)
    return true
  }
  return false
}

function getConversationContactESData(contact: Contact) {
  if (!contact) return {}

  return {
    email: contact.email,
    phone: contact.phone,
    full_name: contact.fullName,
    company_name: contact.companyName,
    profile_photo: contact.profilePhoto,
    tags: contact.tags,
  }
}

export enum CollapsibleItemAction {
  MY_INBOX_ASSIGNED_TO_ME_UNREAD = 'my_inbox_assigned_to_me_unread',
  MY_INBOX_ASSIGNED_TO_ME_ALL = 'my_inbox_assigned_to_me_all',
  MY_INBOX_ASSIGNED_TO_ME_RECENTS = 'my_inbox_assigned_to_me_recents',
  MY_INBOX_ASSIGNED_TO_ME_STARRED = 'my_inbox_assigned_to_me_starred',
  MY_INBOX_INTERNAL_CHAT_UNREAD = 'my_inbox_internal_chat_unread',
  MY_INBOX_INTERNAL_CHAT_ALL = 'my_inbox_internal_chat_all',
  TEAM_INBOX_UNREAD = 'team_inbox_unread',
  TEAM_INBOX_ALL = 'team_inbox_all',
  TEAM_INBOX_RECENTS = 'team_inbox_recents',
  TEAM_INBOX_STARRED = 'team_inbox_starred',
}

export const conversationV2: Module<ConversationState, RootState> = {
  namespaced: true,
  state: {
    all: {
      done: false,
      documents: [],
      last_document_date: '',
      total: 0,
    },
    inbox: {
      done: false,
      documents: [],
      last_document_date: '',
    },
    unread: {
      done: false,
      documents: [],
      last_document_date: '',
    },
    search: {
      done: false,
      documents: [],
      last_document_date: '',
    },
    fetching: false,
    searching: false,
    currentTab: 'all',
    currentTabV2: '',
    searchTerm: '',
    searchTermNewUI: '',
    inboxMenuNewUI: {
      category: '',
      subCategory: '',
      tab: '',
    },
    triggerLinksMenuItem: {},
    filters: {
      user: '',
    },
    filtersV2: {
      assignedTo: '',
      followers: '',
      lastMessageType: '',
      status: 'unread',
      query: '',
      locationId: '',
      startAfterDate: 0,
      sort: 'desc',
      scoreProfile: '',
      sortScoreProfile: '',
      scoreProfileMax: '',
      scoreProfileMin: '',
      conversationType: null,
    },
    onboarding: false,
    popupStep: 1,
    resultsFetchedFromSearch: false,
    outboundSentConversationIds: [],
    newMessageData: null,
    userSavedFilters: null,
    gmbIntegration: null,
    linkedPages: [],
    collapseContent: false,
    draftMessages: {},
    snippets: {},
    phoneNumbers: [],
    whatsAppNumbers: [],
    currentLeftMostTab: CollapsibleItemAction.MY_INBOX_ASSIGNED_TO_ME_UNREAD,
    currentConversationId: '',
    activeLeftMostPanelTab: 'team-inbox',
    connectedFacebookPages: {},
    slaSettings: null,
  },
  getters: {
    meta: (state: ConversationState) => {
      if (state.searchTerm) return state.search
      if (state.currentTab === 'all') return state.all
      if (state.currentTab === 'inbox') return state.inbox
      if (state.currentTab === 'unread') return state.unread
    },
    conversations: (state: ConversationState) => {
      if (state.searchTerm) return state.search.documents
      if (state.currentTab === 'all') return state.all.documents
      if (state.currentTab === 'inbox') return state.inbox.documents
      if (state.currentTab === 'unread') return state.unread.documents
    },
    metaV2: (state: ConversationState) => {
      return state.all
    },
    resultsFetchedFromSearch: (state: ConversationState) => {
      return state.resultsFetchedFromSearch
    },
    conversationsV2: (state: ConversationState) => {
      return Array.isArray(state.all.documents) ? [...state.all.documents] : []
    },
    getTriggerLinksMenuItem: (state: ConversationState) => {
      return state.triggerLinksMenuItem
    },
    currentTabV2: (state: ConversationState) => {
      return state.currentTabV2
    },
    newMessageData: (state: ConversationState) => {
      return state.newMessageData
    },
    leftPanelFilters: (state: ConversationState) => {
      return state?.userSavedFilters?.conversations?.leftPanel
    },
    linkedPages: (state: ConversationState) => {
      return state.linkedPages
    },
    draftMessages: (state: ConversationState) => {
      return state.draftMessages
    },
    getSnippets: (state: ConversationState) => {
      return state.snippets
    },
    phoneNumbers: (state: ConversationState) => {
      return state.phoneNumbers
    },
    whatsAppNumbers: (state: ConversationState) => {
      return state.whatsAppNumbers
    },
    currentLeftMostPanelTab: (state: ConversationState) => {
      return state.currentLeftMostTab
    },
    currentConversationId: (state: ConversationState) => {
      return state.currentConversationId
    },
    activeLeftMostPanelTab: (state: ConversationState) => {
      return state.activeLeftMostPanelTab
    },
    getConnectedFacebookPages: (state: ConversationState) => {
      return state.connectedFacebookPages
    },
    searchTermNewUI: (state: ConversationState) => {
      return state.searchTermNewUI
    },
    inboxMenuNewUI: (state: ConversationState) => {
      return state.inboxMenuNewUI
    },
    getSLASettings: (state: ConversationState) => state.slaSettings,
  },
  mutations: {
    setCurrentConversationId: (state: ConversationState, conversationId: string) => {
      state.currentConversationId = conversationId
    },
    updateLeftMostActiveTab: (state: ConversationState, tab: CollapsibleItemAction) => {
      state.currentLeftMostTab = tab
    },
    updateActiveLeftMostPanelTab: (state: ConversationState, tab: string) => {
      state.activeLeftMostPanelTab = tab
    },
    updateContactInfo: (
      state: ConversationState,
      params: {
        contact: Contact
      }
    ) => {
      if (state.searchTerm) {
        const document = state.search.documents && state.search.documents.find(x => x.contact_id === params.contact.id)
        const updatedSearchDocs = addUpdateContact(
          [...state.search.documents],
          {
            currentTab: 'search',
            payload: {
              ...document,
              ...getConversationContactESData(params.contact),
            },
            dontAdd: true,
          },
          state.filtersV2
        ) // Don't add documents that arent in the searched term array
        if (updatedSearchDocs) {
          state.search.documents = updatedSearchDocs
        }
      }

      let documents: Array<{ [key: string]: any }> = []
      let document: any
      let tabKey: 'unread' | 'inbox' | 'all' | null = null
      if (state.currentTab === 'unread') {
        tabKey = 'unread'
      } else if (state.currentTab === 'inbox') {
        tabKey = 'inbox'
      } else if (state.currentTab === 'all') {
        tabKey = 'all'
      }

      if (tabKey) {
        documents = [...state[tabKey].documents]
        document = state[tabKey].documents && state[tabKey].documents.find(x => x.contact_id === params.contact.id)

        if (document) {
          const updatedDocs = addUpdateContact(
            documents,
            {
              currentTab: state.currentTab,
              payload: {
                ...document,
                ...getConversationContactESData(params.contact),
              },
            },
            state.filtersV2
          )
          if (updatedDocs) {
            state[tabKey].documents = updatedDocs
          }
        }
      }
    },
    addOrUpdate: (
      state: ConversationState,
      params: {
        currentTab: string
        payload: { [key: string]: any }
        dontAdd?: boolean
      }
    ) => {
      if (state.searchTerm) {
        const updatedSearchDocs = addUpdateContact(
          [...state.search.documents],
          { ...params, dontAdd: true },
          state.filtersV2
        ) // Don't add documents that arent in the searched term array
        if (updatedSearchDocs) {
          state.search.documents = updatedSearchDocs
        }
      }

      let tabKey: 'unread' | 'inbox' | 'all' | null = null
      if (params.currentTab === 'unread') {
        tabKey = 'unread'
      } else if (params.currentTab === 'inbox') {
        tabKey = 'inbox'
      } else if (params.currentTab === 'all') {
        tabKey = 'all'
      }

      if (tabKey) {
        const updatedDocs = addUpdateContact([...state[tabKey].documents], params, state.filtersV2)
        if (updatedDocs) {
          state[tabKey].documents = updatedDocs
        }
      }
    },
    addOrUpdateV2: (
      state: ConversationState,
      params: {
        currentTab: string
        payload: { [key: string]: any }
        dontAdd?: boolean
      }
    ) => {
      const clonedDocuments = [...state.all.documents]

      const updatedDocs = addUpdateContact(
        clonedDocuments,
        { ...params, dontAdd: state.searchTerm ? true : false },
        state.filtersV2
      )
      if (!updatedDocs) return
      const uniqueDocs = checkForDuplicacy(updatedDocs)
      state.all.documents = uniqueDocs

      if (typeof state.all.total == 'number') {
        state.all.total = Math.max(state.all.total, uniqueDocs.length)
      }
    },
    updateInternalChats: (
      state: ConversationState,
      params: { currentTab: string; payload: { [key: string]: any }[] }
    ) => {
      if (params.currentTab === 'all') {
        state.all.documents = params.payload
      } else if (params.currentTab === 'unread') {
        state.unread.documents = params.payload
      }
    },
    remove: (state: ConversationState, params: { currentTab: string; payload: { [key: string]: any } }) => {
      if (state.searchTerm) {
        removeContact(state.search.documents, params)
      }
      let documents: Array<{ [key: string]: any }> = []
      if (params.currentTab === 'unread') {
        documents = state.unread.documents
      } else if (params.currentTab === 'inbox') {
        documents = state.inbox.documents
      } else if (params.currentTab === 'all') {
        documents = state.all.documents
      }
      removeContact(documents, params)
    },
    removeV2: (state: ConversationState, params: { currentTab: string; payload: { [key: string]: any } }) => {
      const documents: Array<{ [key: string]: any }> = state.all.documents
      const isContactRemoved = removeContact(documents, params)
      if (state.all.total && isContactRemoved) {
        state.all.total--
      }
    },
    updateNewMessageData: (state: ConversationState, params: { newMessageData: { [key: string]: any } }) => {
      if (params.newMessageData) {
        state.newMessageData = convertObjectKeysCamelCaseToSnakeCase(JSON.parse(JSON.stringify(params.newMessageData)))
      } else {
        state.newMessageData = null
      }
    },

    add: (
      state: ConversationState,
      params: {
        searchTerm: string
        currentTab: string
        results: Array<{ [key: string]: any }>
      }
    ) => {
      let documents: Array<{ [key: string]: any }>
      if (params.searchTerm) {
        documents = state.search.documents
      } else if (params.currentTab === 'unread') {
        documents = state.unread.documents
      } else if (params.currentTab === 'inbox') {
        documents = state.inbox.documents
      } else if (params.currentTab === 'all') {
        documents = state.all.documents
      }

      Object.entries(params.results).forEach(([, value]) => {
        documents.push(
          Object.freeze({
            id: value._id,
            ...value._source,
          })
        )
      })
    },
    addV2: (
      state: ConversationState,
      params: {
        searchTerm: string
        currentTab: string
        results: Array<{ [key: string]: any }>
        total: number
        refresh: boolean
      }
    ) => {
      if (params.refresh) {
        state.all.documents = []
      }
      const documents: Array<{ [key: string]: any }> = state.all.documents
      state.all.total = params.total

      Object.entries(params.results).forEach(([, value]) => {
        documents.push(
          Object.freeze({
            id: value._id,
            ...value,
          })
        )
      })
    },
    tab: (state: ConversationState, currentTab: string) => {
      state.currentTab = currentTab
    },
    term: (state: ConversationState, searchTerm: string) => {
      state.searchTerm = searchTerm
    },
    termNewUI: (state: ConversationState, searchTerm: string) => {
      state.searchTermNewUI = searchTerm
    },
    inboxMenuNewUI: (state: ConversationState, payload: { category: string; subCategory: string; tab: string }) => {
      state.inboxMenuNewUI = { ...payload }
    },
    fetching: (state: ConversationState, fetching: boolean) => {
      state.fetching = fetching
    },
    searching: (state: ConversationState, searching: boolean) => {
      state.searching = searching
    },
    onboarding: (state: ConversationState, onboarding: boolean) => {
      state.onboarding = onboarding
    },
    collapseContent: (state: ConversationState, value: boolean) => {
      state.collapseContent = value
    },

    popupStep: (state: ConversationState, popupStep: number) => {
      state.popupStep = popupStep
    },
    finishedFetch: (
      state: ConversationState,
      payload: {
        searchTerm: string
        currentTab: string
        done: boolean
        last_document_date: number
      }
    ) => {
      if (payload.searchTerm) {
        state.search.done = payload.done
        state.search.last_document_date = payload.last_document_date
      } else if (payload.currentTab === 'unread') {
        state.unread.done = payload.done
        state.unread.last_document_date = payload.last_document_date
      } else if (payload.currentTab === 'inbox') {
        state.inbox.done = payload.done
        state.inbox.last_document_date = payload.last_document_date
      } else if (payload.currentTab === 'all') {
        state.all.done = payload.done
        state.all.last_document_date = payload.last_document_date
      }
    },
    finishedFetchV2: (state: ConversationState, payload: { done: boolean; last_document_date: number }) => {
      state.all.done = payload.done
      state.all.last_document_date = payload.last_document_date
    },
    clear: (state: ConversationState, filterClear: boolean) => {
      state.search = {
        done: false,
        last_document_date: '',
        documents: [],
      }
      state.unread = {
        done: false,
        last_document_date: '',
        documents: [],
      }
      state.inbox = {
        done: false,
        last_document_date: '',
        documents: [],
      }
      state.all = {
        done: false,
        last_document_date: '',
        documents: [],
      }
      state.fetching = false
      if (!filterClear) {
        state.currentTab = 'all'
        state.searchTerm = ''
        locationId = undefined
      }
    },
    clearV2: (state: ConversationState) => {
      state.all = {
        done: false,
        last_document_date: '',
        documents: [],
        total: 0,
      }
      state.fetching = false
    },
    clearAllV2: (state: ConversationState, locationId: string) => {
      if (unsubscribeRealtime) {
        unsubscribeRealtime()
      }
      state.all = {
        done: false,
        last_document_date: '',
        documents: [],
        total: 0,
      }
      state.resultsFetchedFromSearch = false
      state.filtersV2 = {
        assignedTo: '',
        followers: '',
        lastMessageType: '',
        status: 'unread',
        query: '',
        locationId,
        startAfterDate: 0,
        sort: 'desc',
        scoreProfile: '',
        sortScoreProfile: '',
        scoreProfileMax: '',
        scoreProfileMin: '',
      }
      state.outboundSentConversationIds = []
    },
    clearSearchTerm: (state: ConversationState) => {
      state.searchTerm = ''
    },
    clearCurrentTabV2: (state: ConversationState) => {
      state.currentTabV2 = ''
    },
    clearSearch: (state: ConversationState) => {
      state.fetching = false
      state.search = {
        done: false,
        last_document_date: '',
        documents: [],
      }
    },
    setTriggerLinks: (state: ConversationState, links: { value: any; text: string; location_id: string }) => {
      state.triggerLinksMenuItem = links
    },
    setFiltersV2: (
      state: ConversationState,
      filters: {
        status: string
        assignedTo: string
        followers: string
        lastMessageType: string
        sort: string
      }
    ) => {
      state.filtersV2 = filters
    },
    setCurrentTabV2: (state: ConversationState, value: string) => {
      state.currentTabV2 = value
    },
    filterReadsFromUnreadList(state: ConversationState) {
      state.unread.documents = state.unread.documents.filter(doc => doc.unread_count > 0)
    },
    resultsFetchedFromSearch(state: ConversationState, value: boolean) {
      state.resultsFetchedFromSearch = value
    },
    updateOutboundSentConversationIds(state: ConversationState, value: string) {
      //if not inside unread filter
      if (value == null) {
        state.outboundSentConversationIds = []
        return
      }
      if (!state.outboundSentConversationIds.includes(value)) {
        state.outboundSentConversationIds.push(value)
      }
    },
    updateUserSavedFilters(state: ConversationState, value: any) {
      state.userSavedFilters = { ...value }
    },
    setLinkedPages: (state: ConversationState, value: any) => {
      state.linkedPages = value
    },
    updateScore(state: ConversationState, payload: any) {
      const { contactId, profileId, score } = payload
      const conversation = state.all.documents.find(doc => doc.contact_id === contactId)
      if (!conversation) return
      if (!conversation.scoring) {
        conversation.scoring = {}
      }
      conversation.scoring[profileId] = score
    },
    setDraftMessages(state: ConversationState, payload: any) {
      state.draftMessages = payload
    },
    updateContactDrafts(state: ConversationState, { contactId, drafts }: { contactId: string; drafts: any[] }) {
      state.draftMessages = {
        ...state.draftMessages,
        [contactId]: drafts,
      }
    },
    updateSnippets(state: ConversationState, payload: any) {
      state.snippets[payload.locationId] = payload.snippets
    },
    setPhoneNumbers(state: ConversationState, payload: any) {
      state.phoneNumbers = payload
    },
    setwhatsAppNumbers(state: ConversationState, payload: any) {
      state.whatsAppNumbers = payload
    },
    patchParticipantsToCurrentList: (state: ConversationState, payload: any) => {
      try {
        const { index, conversationParticipant } = payload
        if (state.all.documents[index] && state.all.documents[index].id == conversationParticipant.conversationId) {
          state.all.documents[index] = {
            ...state.all.documents[index],
            participants: conversationParticipant.participants,
          }
        }
      } catch (err) {
        console.error('error patching participants', err)
      }
    },
    updateGroupSentMessage(state: ConversationState, payload: { conversationId: string }) {
      const index = state.all.documents.findIndex(doc => doc.id === payload.conversationId)
      if (index > -1) {
        state.all.documents[index] = {
          ...state.all.documents[index],
          startedFromTheUi: false,
        }
      }
    },
    updateInternalChatSentMessage(state: ConversationState, payload: { conversationId: string; participants: any[] }) {
      const index = state.all.documents.findIndex(doc => doc.id === payload.conversationId)
      if (index > -1) {
        state.all.documents[index] = {
          ...state.all.documents[index],
          startedFromTheUi: false,
          participants: payload.participants,
        }

        if (state.all.documents.find(doc => doc.id == NEW_MESSAGE_FLOW_CONVERSATION_ID)) {
          state.all.documents = state.all.documents.filter(doc => doc.id != NEW_MESSAGE_FLOW_CONVERSATION_ID)
        }
      }
    },
    setStartAfterDate(state: ConversationState, value: number | null) {
      state.filtersV2.startAfterDate = value
    },
    setFiltersV2LocationId(state: ConversationState, value: string) {
      state.filtersV2.locationId = value
    },
    setFiltersV2Query(state: ConversationState, value: string) {
      state.filtersV2.query = value
    },
    updateConnectedFacebookPages: (state: ConversationState, payload: any) => {
      if (!state.connectedFacebookPages) {
        state.connectedFacebookPages = {}
      }
      if (payload.locationId) {
        state.connectedFacebookPages[payload.locationId] = payload.pages
      }
    },
    updateConversations: (state: ConversationState, payload: any) => {
      const conversationIdx = state.all.documents.findIndex(x => x.id === payload.id)
      if (conversationIdx !== -1) {
        state.all.documents[conversationIdx] = payload
      }
    },
    updateDocuments: (state: ConversationState, payload: any) => {
      state.all.documents = payload
    },
    updateSLASettings: (state: ConversationState, payload: any) => {
      state.slaSettings = payload
    },
  },
  actions: {
    patchNewGroupChatConversation: (context: ActionContext<ConversationState, RootState>, payload: any) => {
      const existingIndex = context.state.all.documents.findIndex(doc => doc.id === payload.id)

      if (existingIndex > -1) {
        payload.originalLastMessageDate = context.state.all.documents[existingIndex].last_message_date
        payload.originalLastManualMessageDate = context.state.all.documents[existingIndex].last_manual_message_date
        payload.last_message_type = context.state.all.documents[existingIndex].last_message_type
        payload = {
          ...context.state.all.documents[existingIndex],
          ...payload,
        }
        context.commit('removeV2', {
          currentTab: 'all',
          payload: context.state.all.documents[existingIndex],
        })
      }
      context.commit('addOrUpdateV2', {
        currentTab: 'all',
        payload,
      })
    },
    patchNewInternalChatConversation: (context: ActionContext<ConversationState, RootState>, payload: any) => {
      const existingIndex = context.state.all.documents.findIndex(doc => doc.id === payload.id)
      if (existingIndex > -1) {
        payload.originalLastMessageDate = context.state.all.documents[existingIndex].last_message_date
        payload.originalLastManualMessageDate = context.state.all.documents[existingIndex].last_manual_message_date
        payload.last_message_type = context.state.all.documents[existingIndex].last_message_type
        payload = {
          ...context.state.all.documents[existingIndex],
          ...payload,
        }
        context.commit('removeV2', {
          currentTab: 'all',
          payload: context.state.all.documents[existingIndex],
        })
      }
      context.commit('addOrUpdateV2', {
        currentTab: 'all',
        payload,
      })
    },
    updateCurrentConversationId: (context: ActionContext<ConversationState, RootState>, conversationId: string) => {
      context.commit('setCurrentConversationId', conversationId)
    },
    updateLeftMostTab: (context: ActionContext<ConversationState, RootState>, tab: CollapsibleItemAction) => {
      context.commit('updateLeftMostActiveTab', tab)
    },
    updateActiveLeftMostPanelTab: (context: ActionContext<ConversationState, RootState>, tab: string) => {
      context.commit('updateActiveLeftMostPanelTab', tab)
    },
    addNewGroupChatConversation({ commit, state, dispatch }, newConversation) {
      const last_message_type = newConversation.last_message_type ? newConversation.last_message_type : 'TYPE_NO_SHOW'
      newConversation.last_message_date = 2729508541883 // year 2056
      newConversation.last_manual_message_date = 2729508541883 // year 2056

      const alreadyNewGroupConversation = state.all.documents.find(doc => doc.startedFromTheUi)
      if (alreadyNewGroupConversation) {
        const copyOfExisting = Object.assign({}, alreadyNewGroupConversation)
        commit('removeV2', {
          currentTab: 'all',
          payload: { ...alreadyNewGroupConversation },
        })
        if (copyOfExisting.originalLastMessageDate || copyOfExisting.originalLastManualMessageDate) {
          copyOfExisting.last_message_date = copyOfExisting.originalLastMessageDate
          copyOfExisting.last_manual_message_date = copyOfExisting.originalLastManualMessageDate
          copyOfExisting.startedFromTheUi = false
          commit('addOrUpdateV2', { payload: copyOfExisting, currentTab: 'all' })
        }
      }

      dispatch('patchNewGroupChatConversation', { ...newConversation, startedFromTheUi: true, last_message_type })
    },
    addNewInternalChatConversation({ commit, state, dispatch }, newConversation) {
      const last_message_type = newConversation.last_message_type ? newConversation.last_message_type : 'TYPE_NO_SHOW'
      newConversation.last_message_date = 2729508541883 // year 2056
      newConversation.last_manual_message_date = 2729508541883 // year 2056

      const alreadyNewInternalChatConversation = state.all.documents.find(doc => doc.startedFromTheUi)
      if (alreadyNewInternalChatConversation) {
        const copyOfExisting = Object.assign({}, alreadyNewInternalChatConversation)
        commit('removeV2', {
          currentTab: 'all',
          payload: { ...alreadyNewInternalChatConversation },
        })
        if (copyOfExisting.originalLastMessageDate || copyOfExisting.originalLastManualMessageDate) {
          copyOfExisting.last_message_date = copyOfExisting.originalLastMessageDate
          copyOfExisting.last_manual_message_date = copyOfExisting.originalLastManualMessageDate
          copyOfExisting.startedFromTheUi = false
          commit('addOrUpdateV2', { payload: copyOfExisting, currentTab: 'all' })
        }
      }

      dispatch('patchNewInternalChatConversation', { ...newConversation, startedFromTheUi: true, last_message_type })
    },
    closeNewMessageConversation: async (context: ActionContext<ConversationState, RootState>) => {
      if (context.state.newMessageData) {
        if (context.state.newMessageData.is_in_list) {
          const clonedDocuments = [...context.state.all.documents]

          const updatedDocuments = addUpdateContact(
            clonedDocuments,
            {
              currentTab: 'all',
              payload: context.state.newMessageData,
              dontAdd: context.state.searchTerm ? true : false,
            },
            context.state.filtersV2
          )

          if (updatedDocuments) {
            context.commit('updateDocuments', updatedDocuments)
          }
        }
        context.commit('updateNewMessageData', { newMessageData: null })
      }
    },
    closeGroupConversation: (context: ActionContext<ConversationState, RootState>, conversationId: string) => {
      const index = context.state.all.documents.findIndex(doc => doc.id === conversationId)
      if (index > -1) {
        if (context.state.all.documents[index].startedFromTheUi) {
          const itemToRemove = { ...context.state.all.documents[index] }
          context.commit('removeV2', {
            currentTab: 'all',
            payload: itemToRemove,
          })
          if (context.state.all.documents[index].last_message_type != 'TYPE_NO_SHOW') {
            itemToRemove.last_message_date = itemToRemove.originalLastMessageDate
            itemToRemove.last_manual_message_date = itemToRemove.originalLastManualMessageDate
            itemToRemove.startedFromTheUi = false
            context.commit('addOrUpdateV2', {
              currentTab: 'all',
              payload: itemToRemove,
            })
          }
        }
      }
    },
    closeInternalChatConversation: (context: ActionContext<ConversationState, RootState>, conversationId: string) => {
      const index = context.state.all.documents.findIndex(doc => doc.id === conversationId)
      if (index > -1) {
        if (context.state.all.documents[index].startedFromTheUi) {
          const itemToRemove = { ...context.state.all.documents[index] }
          context.commit('removeV2', {
            currentTab: 'all',
            payload: itemToRemove,
          })

          // TODO: remove this after new message flow is deployed
          // if (context.state.all.documents[index].last_message_type != 'TYPE_NO_SHOW') {
          //   itemToRemove.last_message_date = itemToRemove.originalLastMessageDate
          //   itemToRemove.last_manual_message_date = itemToRemove.originalLastManualMessageDate
          //   itemToRemove.startedFromTheUi = false
          //   context.commit('addOrUpdateV2', {
          //     currentTab: 'all',
          //     payload: itemToRemove,
          //   })
          // }
        }
      }
    },
    updateConversationsOnNewMessage: async (
      context: ActionContext<ConversationState, RootState>,
      params: { newMessageData: { [key: string]: any } }
    ) => {
      const currentTab = context.state.currentTab
      if (params.newMessageData && params.newMessageData.contactId && currentTab === 'all') {
        if (params.newMessageData.id === NEW_MESSAGE_FLOW_CONVERSATION_ID) {
          const newConversation = params.newMessageData
          context.commit('updateNewMessageData', {
            newMessageData: { ...newConversation, isInList: false },
          })
        } else {
          let element = context.state.all.documents.filter(
            conversation => conversation.id === params.newMessageData.id
          )[0]

          if (element) {
            context.commit('removeV2', { currentTab: 'all', payload: element })
            context.commit('updateNewMessageData', {
              newMessageData: { ...element, isInList: true },
            })
          } else {
            element = await Conversation.getById(params.newMessageData.id)

            const contactDetails = await Contact.getById(params.newMessageData.contactId)

            const lastMessageType = MessageTypeString[MessageType[element._data.last_message_type]]

            element = {
              id: element._id,
              ...element._data,
              fullName: contactDetails.fullName,
              contactName: contactDetails.name,
              email: contactDetails.email,
              phone: contactDetails.phone,
              lastMessageType,
              scoring: contactDetails.scoring,
            }

            context.commit('updateNewMessageData', {
              newMessageData: { ...element, isInList: false },
            })
          }
        }
      }
    },
    submitFiltersV2: async (
      context: ActionContext<ConversationState, RootState>,
      params: {
        filters: any
        refresh: boolean
        locationId?: string
        tab: string
        newMessageData?: any
        skipUpdateFilter?: boolean
      }
    ) => {
      if (params.filters.sort != context.state.filtersV2.sort) {
        params.refresh = true
      }

      if (params.refresh) {
        context.commit('clearV2')
      }
      if (!params.skipUpdateFilter) {
        context.commit('setFiltersV2', params.filters)
      }
      context.commit('setCurrentTabV2', params.tab)

      if (params.locationId) {
        locationId = params.locationId
      }

      await context.dispatch('loadMoreV2', {
        refresh: params.refresh,
        newMessageData: params.newMessageData,
        skipUpdateFilter: params.skipUpdateFilter,
        filters: params.filters,
      })
    },
    submitFilters: async (context: ActionContext<ConversationState, RootState>, filters) => {
      context.commit('clear', true)
      context.commit('setFilters', filters)
      await Promise.all([
        context.dispatch('loadMore', { currentTab: 'all' }),
        context.dispatch('loadMore', { currentTab: 'inbox' }),
        context.dispatch('loadMore', { currentTab: 'unread' }),
      ])
    },
    markAsReadRemoveFromUnreads: async (
      context: ActionContext<ConversationState, RootState>,
      params: { conversationId: string }
    ) => {
      context.commit('remove', {
        currentTab: 'unread',
        payload: { id: params.conversationId },
      })
    },
    fetchTriggerLinks: async (context: ActionContext<ConversationState, RootState>, params: { locationId: string }) => {
      let _locationId = params?.locationId || locationId
      if (
        _locationId &&
        (!context.state.triggerLinksMenuItem.location_id ||
          context.state.triggerLinksMenuItem.location_id !== _locationId)
      ) {
        const snapshot = await Link.fetchAllByLocation(_locationId).get()
        const links = snapshot.docs
          .map((d: any) => new Link(d))
          .filter(l => !l.isInternal)
          .map(l => {
            return {
              text: l.name,
              value: `{{trigger_link.${l.id}}}`,
            }
          })
        context.commit('setTriggerLinks', {
          menu: links,
          text: 'Trigger links',
          location_id: _locationId,
        })
        return { menu: links, text: 'Trigger links', location_id: _locationId }
      } else {
        return context.state.triggerLinksMenuItem || {}
      }
    },
    syncAllV2: async (
      context: ActionContext<ConversationState, RootState>,
      params: {
        locationId: string
        noListener?: boolean
        stickyFilterEnabled?: boolean
      }
    ) => {
      context.commit('clearAllV2', params.locationId)
      if (locationId != params.locationId || !params.stickyFilterEnabled) {
        context.commit('clearSearchTerm')
        context.commit('clearCurrentTabV2')
      }
      locationId = params.locationId
      if (params.noListener) return
      await context.dispatch('startListeningV2')
      await context.dispatch('startListeningToParticipants')
    },
    setCurrentTab: async (context: ActionContext<ConversationState, RootState>, currentTab: string) => {
      context.commit('tab', currentTab)
      if (context.state.searchTerm) {
        context.commit('clearSearch')
        context.commit('searching', true)
        await context.dispatch('loadMore')
      }
    },
    setSearchTerm: async (context: ActionContext<ConversationState, RootState>, searchTerm: string) => {
      context.commit('term', searchTerm)
      context.commit('clearSearch')
      if (!searchTerm) {
        context.commit('searching', false)
        context.commit('fetching', false)
        return
      }

      context.commit('searching', true)
      if (searchDebouncer) clearTimeout(searchDebouncer)
      searchDebouncer = setTimeout(() => {
        context.dispatch('loadMore')
        searchDebouncer = null
      }, 300)
    },
    setSearchTermV2: async (context: ActionContext<ConversationState, RootState>, searchTerm: string) => {
      context.commit('term', searchTerm)
      if (searchTerm.length > 0 && searchTerm.length < 3) {
        context.commit('searching', false)
        context.commit('fetching', false)
        return
      }

      context.commit('clearV2')
      context.commit('searching', true)
      if (searchDebouncer) clearTimeout(searchDebouncer)
      searchDebouncer = setTimeout(() => {
        context.dispatch('loadMoreV2', { refresh: true })
        searchDebouncer = null
      }, 300)
    },
    setSearchTermForNewUI: (context: ActionContext<ConversationState, RootState>, searchTerm: string) => {
      context.commit('termNewUI', searchTerm)
    },
    setInboxForNewUI: (
      context: ActionContext<ConversationState, RootState>,
      payload: { category: string; subCategory: string; tab: string }
    ) => {
      context.commit('inboxMenuNewUI', payload)
    },
    setOnboarding: async (context: ActionContext<ConversationState, RootState>, onboarding: boolean) => {
      context.commit('onboarding', onboarding)
    },
    setCollapseContent: async (context: ActionContext<ConversationState, RootState>, value: boolean) => {
      context.commit('collapseContent', value)
    },

    setPopupStep: async (context: ActionContext<ConversationState, RootState>, popupStep: number) => {
      context.commit('popupStep', popupStep)
    },

    loadMoreV2: async (
      context: ActionContext<ConversationState, RootState>,
      params: { refresh: boolean; newMessageData?: any; skipUpdateFilter?: boolean; filters?: any }
    ) => {
      const currentTab = context.state.currentTab
      const data = context.state.all

      //maintaining pagination
      if (!params.refresh && data.last_document_date) {
        context.commit('setStartAfterDate', data.last_document_date)
      } else {
        context.commit('setStartAfterDate', null)
      }

      context.commit('fetching', true)
      const localSeqFetching = ++seqFetching
      context.commit('setFiltersV2LocationId', locationId)
      const searchTerm = context.state.searchTerm

      if (searchTerm && searchTerm.length >= 3) {
        context.commit('setFiltersV2Query', searchTerm)
      } else {
        context.commit('setFiltersV2Query', null)
      }

      if (params.refresh && isComingFromSearch(context.state.filtersV2)) {
        context.commit('resultsFetchedFromSearch', true)
      } else {
        context.commit('resultsFetchedFromSearch', false)
      }

      let requestFilters = context.state.filtersV2
      if (params.skipUpdateFilter) {
        requestFilters = params.filters
        requestFilters.locationId = locationId
      }
      const results = await Conversation.getConversationsV2(requestFilters)

      if (searchTerm && localSeqFetching !== seqFetching) return

      context.commit('searching', false)
      context.commit('fetching', false)
      results.conversations = convertObjectKeysCamelCaseToSnakeCase(JSON.parse(JSON.stringify(results.conversations)))
      results.conversations.forEach(c => {
        const obj = {}
        c.scoring?.forEach(s => {
          obj[s.id] = s.score
        })
        c.scoring = { ...obj }
      })

      context.commit('addV2', {
        currentTab,
        searchTerm,
        results: results.conversations,
        total: results.total,
        refresh: params.refresh,
      })

      let lastDocumentDate = (results.conversations[results.conversations.length - 1] as any)?.last_message_date

      if (['last_manual_message_date', 'score_profile'].includes(context.state?.filtersV2?.sortBy)) {
        lastDocumentDate = (results.conversations[results.conversations.length - 1] as any)?.sort
      }

      context.commit('finishedFetchV2', {
        done: results.conversations.length !== limit,
        last_document_date: results.conversations.length > 0 ? lastDocumentDate : '',
      })

      if (params.newMessageData && params.newMessageData.contactId && currentTab === 'all') {
        context.dispatch('updateConversationsOnNewMessage', {
          newMessageData: params.newMessageData,
        })
      }
    },
    startListeningV2: async (context: ActionContext<ConversationState, RootState>) => {
      firstSnapshot = true
      const currentUser = await context.dispatch('user/get', null, {
        root: true,
      })
      if (unsubscribeRealtime) unsubscribeRealtime()
      unsubscribeRealtime = Conversation.collectionRef()
        .where('location_id', '==', locationId)
        .orderBy('date_updated', 'desc')
        .limit(10)
        .onSnapshot(
          async (snapshot: firebase.firestore.QuerySnapshot) => {
            if (firstSnapshot) {
              firstSnapshot = false
              return
            }

            for (let i = snapshot.docChanges().length - 1; i >= 0; i--) {
              const docChange = snapshot.docChanges()[i]
              let data: { [key: string]: any } = {
                id: docChange.doc.id,
                ...docChange.doc.data(),
              }

              try {
                if (data.date_added) data.date_added = data.date_added.toMillis()
                if (data.date_updated) data.date_updated = data.date_updated.toMillis()
                if (data.last_message_date) data.last_message_date = data.last_message_date.toMillis()

                if (data.last_manual_message_date)
                  data.last_manual_message_date = data.last_manual_message_date.toMillis()
              } catch {}

              if (docChange.type === 'removed') continue

              if (typeof data.last_message_type == 'number') {
                data.last_message_type = MessageType[data.last_message_type]
              }
              if (checkIfConversationToBeRemoved(context.state, data, currentUser)) {
                context.commit('removeV2', { currentTab: 'all', payload: data })
                continue
              }

              //dedup contact fetch by storing contact data in vuex
              if (data.contact_id) {
                const contact = await context.dispatch('contacts/syncGet', data.contact_id, { root: true })
                data = Object.assign(getConversationContactESData(contact), data)
              }

              if (context.state.newMessageData && data.contact_id === context.state.newMessageData.contact_id) {
                context.commit('updateNewMessageData', { newMessageData: null })
                // context.dispatch('closeNewMessageConversation', {conversationId: data.id})
                if (!data.is_last_message_internal_comment) {
                  continue
                }
              }

              if (data.type == 5) {
                /*
              find out the conversation is already in the list or not
              if not fetch from ES calling /search api
              use the existing data to add participants, altid, type etc
              */
                let conversation = context.state.all.documents.find(c => c.id === data.id)

                if (!conversation) {
                  // fetch from ES & commit
                  const results = await Conversation.getConversationsV2({
                    id: data.id,
                    locationId: locationId,
                  })
                  conversation = results.conversations.find(c => c.id === data.id)

                  if (!conversation) return
                  data = {
                    ...conversation,
                    ...data,
                    participants: conversation?.participants,
                    full_name: conversation?.full_name || conversation?.fullName,
                    type: 'TYPE_GROUP_SMS',
                  }
                } else {
                  data = {
                    ...conversation,
                    ...data,
                    participants: conversation?.participants,
                    full_name: conversation?.full_name || conversation?.fullName,
                    type: 'TYPE_GROUP_SMS',
                  }
                }
              }

              // internal chat related changes
              if (data.type == 6) {
                if (!data.participants || !data.participants.length) {
                  const participantId = data.conversation_participant_id
                  if (participantId) {
                    const reqResponse = await ConversationParticipants.getById(participantId)
                    if (reqResponse)
                      data.participants = reqResponse.participants || reqResponse?._data?.participants || []
                  }
                }

                // if the current user is not a participant, continue
                if (!data.participants?.find((p: any) => p.id == currentUser.id)) continue

                const conversation = context.state.all.documents.find(c => c.id === data.id)
                if (!data.last_message_body) data.startedFromTheUi = conversation?.startedFromTheUi

                // update the participants unread_count
                data.participants.forEach((participant: any) => {
                  if (participant.id == currentUser.id) data.unread_count = participant.unread_count
                })
              }

              // update the active left most panel tab
              let canUpdateData = true
              const isInternalChatEnabled =
                context.rootState.featureFlags?.locationFeatureFlags?.conversations?.internalChat
              if (isInternalChatEnabled) {
                if (context.state.activeLeftMostPanelTab == 'internal-chat' && data.type != 6) {
                  canUpdateData = false
                } else if (context.state.activeLeftMostPanelTab != 'internal-chat' && data.type == 6) {
                  canUpdateData = false
                }
              }

              if (canUpdateData) {
                context.commit('addOrUpdateV2', {
                  currentTab: 'all',
                  payload: data,
                })
              }
            }
          },
          err => {
            console.error(err)
            throw err
          }
        )
    },
    startListeningToParticipants: async (context: ActionContext<ConversationState, RootState>) => {
      firstSnapshot = true
      if (cancelParticipantSubscription) {
        cancelParticipantSubscription()
      }

      const currentUser = await context.dispatch('user/get', null, {
        root: true,
      })
      cancelParticipantSubscription = ConversationParticipants.getByLocationId(locationId as string).onSnapshot(
        (snapshot: firebase.firestore.QuerySnapshot) => {
          snapshot.docChanges().forEach(docChange => {
            if (firstSnapshot) {
              firstSnapshot = false
              return
            }

            const isNew = docChange.oldIndex == -1
            const conversationParticipant = new ConversationParticipants(docChange.doc)
            const isDeleted = conversationParticipant.deleted
            const indexInCurrentList = context.state.all.documents.findIndex(
              doc => doc.id == conversationParticipant.conversationId
            )

            if (indexInCurrentList == -1 || isNew || isDeleted) {
              // these changes would be handled from conversations listener
              return
            }

            context.commit('patchParticipantsToCurrentList', { index: indexInCurrentList, conversationParticipant })

            // read / unread handling
            if (docChange.type == 'modified') {
              const data = docChange.doc.data()
              if (data.type === 1) {
                let isThereAnyUnreadCountChange = false
                const tempConversations = JSON.parse(JSON.stringify(context.state.all.documents))
                tempConversations.forEach((conversation: any) => {
                  if (conversation.id == data.conversation_id) {
                    const participant = conversation.participants.find((p: any) => p.id == currentUser.id)
                    const tempCUC = conversation.unread_count
                    const tempPUC = participant.unread_count
                    if (participant && tempCUC != tempPUC) {
                      isThereAnyUnreadCountChange = true
                      conversation.unread_count = participant.unread_count
                    }

                    // if the participants are changed, update the participants
                    if (conversation.participants.length !== data.participants.length) {
                      isThereAnyUnreadCountChange = true
                      conversation.participants = data.participants
                    }
                  }
                })

                if (isThereAnyUnreadCountChange) {
                  context.commit('updateInternalChats', {
                    currentTab: 'all',
                    payload: tempConversations,
                  })
                }
              }
            }
          })
        },
        err => {
          console.error(err)
          throw err
        }
      )
    },
    bulkAction: async (context: ActionContext<ConversationState, RootState>, data: IConversationBulkAction) => {
      const results = await Conversation.performBulkAction(data)
      let type
      if (data.value) {
        type =
          data.action == ConversationBulkActionTypes.MarkAsRead
            ? 'unread'
            : data.action == ConversationBulkActionTypes.Starred
            ? 'starred'
            : 'delete'
      } else {
        type =
          data.action == ConversationBulkActionTypes.MarkAsRead
            ? 'read'
            : ConversationBulkActionTypes.Archive
            ? 'archive'
            : 'unstarred'
      }
      results.type = type

      if (results.success) {
        let message = ''
        if (results.type == 'read') {
          message = `Success: ${results.count} Conversation${results.count > 1 ? 's' : ''} marked as read`
        } else if (results.type == 'unread') {
          message = `Success: ${results.count} Conversation${results.count > 1 ? 's' : ''} marked as unread`
        } else if (results.type == 'starred') {
          message = `Success: ${results.count} Conversation${results.count > 1 ? 's' : ''} marked as starred`
        } else if (results.type == 'unstarred') {
          message = `Success: ${results.count} Conversation${results.count > 1 ? 's' : ''} marked as unstarred`
        } else if (results.type == 'archive') {
          message = `Success: ${results.count} Conversation${results.count > 1 ? 's' : ''} archived`
        } else {
          message = `Success: ${results.count} Conversation${results.count > 1 ? 's' : ''} deleted`
        }

        return { success: true, message, type }
      } else {
        return {
          success: false,
          message: 'Unable to perform bulk actions',
          type,
        }
      }
    },
    fakeUpdateAfterBulkActions: (
      context: ActionContext<ConversationState, RootState>,
      params: { conversationIds: string[]; type: string }
    ) => {
      const bulkActionConversations: any[] = JSON.parse(
        JSON.stringify(
          context.state.all.documents.filter((doc: any) => {
            return params.conversationIds.findIndex(id => id == doc.id) > -1
          })
        )
      ).map(conversation => {
        switch (params.type) {
          case 'read':
            conversation.unread_count = 0
            break
          case 'unread':
            conversation.unread_count = 1
            break
          case 'delete':
            conversation.deleted = true
            break
          case 'starred':
            conversation.starred = true
            break
          case 'unstarred':
            conversation.starred = false
            break
        }

        return conversation
      })

      for (const conversation of bulkActionConversations) {
        if (checkIfConversationToBeRemoved(context.state, conversation)) {
          context.commit('removeV2', {
            currentTab: 'all',
            payload: conversation,
          })
          continue
        }

        context.commit('addOrUpdateV2', {
          currentTab: 'all',
          payload: conversation,
        })
      }

      if (context.state.all.documents.length == 0) {
        context.commit('fetching', true)
        if (fakeBulkActionDebouncer) clearTimeout(fakeBulkActionDebouncer)
        fakeBulkActionDebouncer = setTimeout(async () => {
          await context.dispatch('loadMoreV2', { refresh: true })
          fakeBulkActionDebouncer = null
        }, 3000)
      }
    },
    refreshUnread: (context: ActionContext<ConversationState, RootState>) => {
      context.commit('filterReadsFromUnreadList')
    },
    updateSentMessage: (context: ActionContext<ConversationState, RootState>, conversationId: string) => {
      if (context.state.filtersV2.status == 'unread') {
        context.commit('updateOutboundSentConversationIds', conversationId)
      } else {
        context.commit('updateOutboundSentConversationIds', null)
      }
    },
    logout: {
      root: true,
      handler: async () => {
        if (unsubscribeRealtime) {
          unsubscribeRealtime()
        }
      },
    },
    fetchUserSavedFilters: async (
      context: ActionContext<ConversationState, RootState>,
      params: { userId: string; locationId: string }
    ) => {
      const filterData = await ConversationsService.fetchFilters(params.userId, params.locationId)
      context.commit('updateUserSavedFilters', filterData)
      context.commit('setCurrentTabV2', filterData.conversations?.leftPanel?.status)
    },
    updateUserSavedFiltersLocally: (context: ActionContext<ConversationState, RootState>, data: any) => {
      context.commit('updateUserSavedFilters', data)
    },
    async getLinkedPages(context: ActionContext<ConversationState, RootState>, locationId: string) {
      try {
        const pages = await IntegrationService.GMB.getLinkedPages(locationId)

        if (pages.linkedPages) {
          context.commit('setLinkedPages', pages.linkedPages)
        }
      } catch (err) {
        console.error('Error fetching linked pages', err)
      }
    },
    updateProfileScore: (context: ActionContext<ConversationState, RootState>, params: any) => {
      context.commit('updateScore', params)
    },

    async fetchDraftMessages(
      context: ActionContext<ConversationState, RootState>,
      params: { contactId: string; locationId: string }
    ) {
      const response = await ConversationsService.getDraftMessages(params.locationId, params.contactId)
      if (response && response.drafts) {
        context.commit('updateContactDrafts', {
          contactId: params.contactId,
          drafts: response.drafts || [],
        })
      }
      return response?.drafts || []
    },

    async saveDraftMessage(context: ActionContext<ConversationState, RootState>, body: any) {
      // save to existing drafts or create new drafts
      const contactId = body.contactId
      const contactDrafts = context.state.draftMessages[contactId] || []
      if (contactDrafts.length) {
        const channelDM = body.providerId
          ? contactDrafts.find(
              draft => body.providerId && draft.providerId === body.providerId && draft.channel === body.channel
            )
          : contactDrafts.find(draft => !body.providerId && draft.channel === body.channel)

        if (!channelDM) contactDrafts.push(body)
        else {
          contactDrafts.forEach(draft => {
            if (body.providerId && draft.providerId === body.providerId && draft.channel === body.channel) {
              Object.assign(draft, body)
            } else if (!body.providerId && !draft.providerId && draft.channel === body.channel) {
              Object.assign(draft, body)
            }
          })
        }

        const allDrafts = context.state.draftMessages || {}
        allDrafts[contactId] = contactDrafts || []
        context.commit('setDraftMessages', allDrafts)
      } else {
        const allDrafts = context.state.draftMessages || {}
        allDrafts[contactId] = [body]
        context.commit('setDraftMessages', allDrafts)
      }

      if (draftsDebouncer) clearTimeout(draftsDebouncer)
      draftsDebouncer = setTimeout(async () => {
        try {
          await ConversationsService.saveDraftMessage(body)
        } catch (error) {
          console.log('Error saving draft message', error)
        }
        draftsDebouncer = null
      }, 500)
    },

    async deleteDraftMessage(
      context: ActionContext<ConversationState, RootState>,
      params: { locationId: string; contactId: string; channel: string; providerId?: string; draftId?: string }
    ) {
      try {
        if (!params.contactId) return
        if (params.draftId) {
          await ConversationsService.deleteDraftMessageById(params.draftId)
        } else {
          await ConversationsService.deleteDraftMessageByContactChannel(
            params.locationId,
            params.contactId,
            params.channel,
            params.providerId
          )
        }

        // reset session drafts
        const allDrafts = context.state.draftMessages || {}
        let contactDrafts = allDrafts[params.contactId] || []
        if (params.draftId) {
          contactDrafts = contactDrafts.filter(x => x._id !== params.draftId)
        } else if (params.providerId) {
          contactDrafts = contactDrafts.filter(x => x.providerId !== params.providerId && x.channel !== params.channel)
        } else {
          contactDrafts = contactDrafts.filter(x => x.channel !== params.channel || x.providerId)
        }

        allDrafts[params.contactId] = contactDrafts
        context.commit('setDraftMessages', allDrafts)
      } catch (error) {
        console.log('Error deleting draft message', error)
      }
    },

    async fetchPhoneNumbers(context: ActionContext<ConversationState, RootState>, params: { locationId: string }) {
      let phoneNumbers: any = []
      try {
        const response = await PhoneSystemService.getPhoneNumbers(params.locationId)
        phoneNumbers = response?.data?.numbers || []
        context.commit('setPhoneNumbers', phoneNumbers)
      } catch (error) {
        console.error('Error fetching phone numbers', error)
      }
      return phoneNumbers
    },

    async fetchWhatsAppNumbers(context: ActionContext<ConversationState, RootState>, params: { locationId: string }) {
      let whatsAppNumbers: any = []
      try {
        const response = await PhoneSystemService.getWhatsAppNumbers(params.locationId)
        whatsAppNumbers = response?.data || []
        context.commit('setwhatsAppNumbers', whatsAppNumbers)
      } catch (error) {
        console.error('Error fetching whatsApp numbers', error)
      }
      return whatsAppNumbers
    },

    updatePhoneNumbers: (context: ActionContext<ConversationState, RootState>, params: any) => {
      context.commit('setPhoneNumbers', params)
    },
    updateOnGroupChatMessageSent(
      context: ActionContext<ConversationState, RootState>,
      params: { conversationId: string }
    ) {
      context.commit('updateGroupSentMessage', params)
    },
    updateOnInternalChatMessageSent(
      context: ActionContext<ConversationState, RootState>,
      params: { conversationId: string; participants: any[] }
    ) {
      context.commit('updateInternalChatSentMessage', params)
    },
    cancelParticipantsSubscription() {
      if (cancelParticipantSubscription) cancelParticipantSubscription()
    },
    cancelConversationsSubscription() {
      if (unsubscribeRealtime) unsubscribeRealtime()
    },
    setSLASettings: (context: ActionContext<ConversationState, RootState>, payload: any) => {
      context.commit('updateSLASettings', payload)
    },
  },
}
